import { observer, useLocalObservable } from 'mobx-react-lite';
import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useStore } from '../../../../hooks/useStore';
import { TableDataGrid } from '../../../../lib/components/compound/tableDataGrid';
import Util from '../../../../misc/util';
import { buildImportColumns, ImportColumnDef, TableProps } from './columns';

export const ImportsTable: FC<TableProps> = observer((props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { impexStore, companyStore } = useStore();

  const importColumns = buildImportColumns(t, navigate);

  type UIStore = {
    isLoading?: boolean;
    importList: ImportColumnDef[];
    load: () => Promise<void>;
    startLoading: () => Promise<void>;
    finishLoading: () => void;
  };

  const uiStore = useLocalObservable<UIStore>(() => ({
    isLoading: undefined,
    importList: [],
    async load() {
      if (!companyStore.company || this.isLoading) return;
      await this.startLoading();
      this.finishLoading();
    },
    startLoading() {
      this.isLoading = true;
      return impexStore.fetchImportList();
    },
    finishLoading() {
      this.importList = impexStore.ImportList.map((i) => ({
        id: i.id,
        name: i.file.name,
        type: i.import_type,
        created_at: new Date(i.created_at),
        time: new Date(i.created_at),
        owner: i.user,
        fileType: Util.getFileExtension(i.file.name) ?? '?',
        fileName: i.file.name,
        fileUrl: i.file.url,
        status: i.status,
        errrorCount: i.errors ? Util.getKeys(i.errors).length : 0,
        errorMessages: Array.isArray(i.errors)
          ? (i.errors as string[]).filter((msg) => typeof msg === 'string')
          : []
      }));
      this.isLoading = false;
    }
  }));

  useEffect(() => {
    uiStore.load();
  }, [companyStore.company]);

  return (
    <TableDataGrid
      onRowDoubleClick={(row) => {
        navigate(`/imports/${row.id}`);
      }}
      loading={uiStore.isLoading}
      rows={uiStore.importList}
      columns={importColumns}
      checkboxSelection={false}
      initialState={{ sorting: { sortModel: [{ field: 'created_at', sort: 'desc' }] } }}
      {...props}
    />
  );
});
